/**
 * @file Hook wrapping the `cancelSignifyFetchJob` callable. Consumer: `useSignifyFetchJobProgress`.
 * The callable writes `phase: 'cancelled'` itself, so the header flips as soon as this resolves
 * rather than waiting for the worker's next poll; parts already in flight still finish.
 * `cancelling` keeps a second press from issuing a second call while the first is unanswered, and
 * `execute` rethrows the callable's own error so the caller sees the `HttpsError` intact.
 */

import { useCallback } from 'react';
import type { CancelSignifyFetchJobRequest, CancelSignifyFetchJobResponse } from '#types';
import { cancelSignifyFetchJob } from '../services/signifyApiService';
import { useSignifyApi } from './useSignifyApi';

export const useCancelSignifyFetchJob = () => {
  const api = useSignifyApi<CancelSignifyFetchJobResponse>();

  const cancelFetchJob = useCallback(
    async (request: CancelSignifyFetchJobRequest): Promise<CancelSignifyFetchJobResponse> =>
      api.execute(() => cancelSignifyFetchJob(request)),
    [api],
  );

  return {
    cancelling: api.loading,
    error: api.error,
    cancelFetchJob,
  };
};
